import React from 'react';
import { FormFieldRenderer } from './form-field-renderer';
import { AccordionFormRenderer } from './accordion-form-renderer';
import { ActionButton } from '@doer/frontend-components/buttons';
import { Spinner } from '@doer/frontend-components/spinner';
import { CreativityType } from '../../types/banner';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { useTemplateConfig } from '../../hooks/banners/use-template-config';
import type { FormField, RoutedFormData } from '../../types/templates';
import type { FieldChangeHandler } from '../../types/form-renderer';
import { isFieldVisibleInResolution } from '../../utils/resolution-fields';

interface CreativityFormProps {
  creativityType: CreativityType;
  formData: RoutedFormData;
  errors: Record<string, string>;
  fields: readonly FormField[];
  isFormComplete: boolean;
  onFieldChange: FieldChangeHandler;
  setFieldError: (key: string, error?: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  showSubmit?: boolean;
  /** Active resolution in the live editor */
  activeResolutionId?: string;
}

export const CreativityForm: React.FC<CreativityFormProps> = ({
  creativityType,
  formData,
  errors,
  fields,
  isFormComplete,
  onFieldChange,
  setFieldError,
  onSubmit,
  submitLabel = 'Export',
  isSubmitting = false,
  showSubmit = true,
  activeResolutionId
}) => {
  const { spacing } = useResponsiveSizes();

  const template = useTemplateConfig(creativityType);
  const rendererType = template?.renderConfig?.renderer ?? 'default';

  const allFields: readonly FormField[] = template?.formFields ?? fields;
  const visibleFields = allFields.filter(field => isFieldVisibleInResolution(field, activeResolutionId));

  const renderField = (field: FormField) => (
    <FormFieldRenderer
      key={field.key}
      field={field}
      formData={formData}
      onFieldChange={onFieldChange}
      errors={errors}
      creativityType={creativityType}
      setFieldError={setFieldError}
    />
  );

  const renderFields = () => {
    switch (rendererType) {
      case 'accordion':
        return (
          <AccordionFormRenderer
            fields={visibleFields}
            formData={formData}
            onFieldChange={onFieldChange}
            errors={errors}
            creativityType={creativityType}
          />
        );
      default:
        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {visibleFields.map(renderField)}
          </div>
        );
    }
  };

  return (
    <>
      {renderFields()}
      <div style={{ width: '100%', marginTop: spacing.sm }}>
        {showSubmit && (
          <div style={{ width: '100%', marginTop: spacing.sm }}>
            <ActionButton
              variant='primary'
              size='L'
              fullWidth
              onClick={onSubmit}
              disabled={isSubmitting || !isFormComplete}
            >
              {isSubmitting ? <Spinner size={16} /> : submitLabel}
            </ActionButton>
          </div>
        )}
      </div>
    </>
  );
};
